import { useCallback, useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, num } from "@/lib/api";

export const RecorderPanel = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const load = useCallback(() => {
    api.get("/recorder").then((r) => setSt(r.data)).catch(() => {});
    api.get("/recorder/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const snap = async () => {
    setBusy(true);
    try { const r = await api.post("/recorder/snapshot"); toast.success(`Snapshot saved: ${r.data.strikes} strikes @ ${num(r.data.spot)}`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="panel p-4" data-testid="panel-option-recorder">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Option price recorder</span>
        <span data-testid="recorder-state" className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wider ${
          st?.recorder_enabled && st?.market_open ? "border-emerald-500/40 text-emerald-400" : "border-slate-600 text-slate-400"}`}>
          {!st?.recorder_enabled ? "OFF" : st?.market_open ? "RECORDING" : "IDLE · MARKET CLOSED"}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div><div className="eyebrow !text-[10px]">Days</div><div className="num text-lg font-semibold" data-testid="recorder-days">{st?.days ?? "—"}</div></div>
        <div><div className="eyebrow !text-[10px]">Snapshots</div><div className="num text-lg font-semibold" data-testid="recorder-snapshots">{st?.snapshots ?? "—"}</div></div>
        <div><div className="eyebrow !text-[10px]">Every</div><div className="num text-lg font-semibold">{st?.recorder_interval_min ?? "—"}m</div></div>
      </div>
      <div className="num mt-2 text-[11px] text-slate-500" data-testid="recorder-last">
        Last: {st?.last ? `${st.last.date} ${st.last.tod} · exp ${st.last.expiry} · ${st.last.source}` : "none yet"}
      </div>
      {st?.recent_days?.length > 0 && (
        <div className="mt-2 max-h-24 overflow-auto text-[11px] text-slate-400">
          {st.recent_days.map((d) => <div key={d.date} className="num flex justify-between"><span>{d.date}</span><span>{d.count} · {d.first}–{d.last}</span></div>)}
        </div>
      )}
      {health?.days?.length > 0 && (
        <div className="mt-3" data-testid="recorder-health">
          <div className="eyebrow !text-[10px]">Day health · {health.problems} problem day{health.problems === 1 ? "" : "s"} since {health.since}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {health.days.map((d) => (
              <span key={d.date} title={d.holiday !== undefined ? `${d.date}: NSE holiday · ${d.holiday}` : `${d.date}: ${d.count} snapshots (${d.status})`} data-testid={`recorder-day-${d.date}`}
                className={`num rounded px-1 py-0.5 text-[10px] ${d.status === "holiday" ? "bg-slate-700/40 text-slate-400" : d.status === "ok" ? "bg-emerald-500/15 text-emerald-400" : d.status === "low" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
                {d.date.slice(5)} · {d.status === "holiday" ? "holiday" : d.count}
              </span>
            ))}
          </div>
        </div>
      )}
      <Button data-testid="button-snapshot-now" size="sm" variant="outline" disabled={busy} onClick={snap} className="mt-3 h-7 w-full gap-1 border-slate-700 text-xs">
        <Camera size={12} /> {busy ? "Saving…" : "Snapshot now"}
      </Button>
      <p className="mt-2 text-[11px] leading-snug text-slate-500">Saves ATM ±15 strikes of the nearest expiry during market hours. Backtests with "Recorded" pricing use these real prices.</p>
    </div>
  );
};
